import { t } from "i18next";
import { CheckIcon, UsersIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import type { CreateAccountState } from "@/hooks/accounts/useCreateAccountStore";
import { cn } from "@/lib/utils";
import type { CreateInsuranceFormData } from "@/types/insurance/insurance";

interface GroupDropdownMenuProps {
	store: CreateAccountState;
	insurance: CreateInsuranceFormData;
}

export const GroupDropdownMenu = ({
	store,
	insurance,
}: GroupDropdownMenuProps) => {
	const selectedGroups = insurance.groups;

	const hasGroupError =
		insurance.error === "The group already has this insurance";

	// Sort groups alphabetically, grouping by associated account
	const sortedGroups = [...store.groups].sort((a, b) => {
		const accountA = store.accounts.find(
			(account) => account.id === a.accountID,
		);
		const accountB = store.accounts.find(
			(account) => account.id === b.accountID,
		);

		if (accountA && accountB) {
			if (accountA.name < accountB.name) return -1;
			if (accountA.name > accountB.name) return 1;
		}

		if (a.name < b.name) return -1;
		if (a.name > b.name) return 1;

		return 0;
	});

	// Organize groups by account for display in the popover
	const groupsByAccount = store.accounts
		.map((account) => {
			const accountsWithGroups = {
				accountName: account.name,
				accountId: account.id,
				isAll:
					store.groups.filter(
						(group) =>
							group.accountID === account.id &&
							selectedGroups.includes(group.id),
					).length ===
					store.groups.filter((group) => group.accountID === account.id).length,
				groups: store.groups
					.filter(
						(group) =>
							group.accountID === account.id &&
							selectedGroups.includes(group.id),
					)
					.sort((a, b) => a.name.localeCompare(b.name)),
			};

			if (accountsWithGroups.groups.length > 0) {
				return accountsWithGroups;
			}

			return null;
		})
		.filter((account) => account !== null)
		.sort((a, b) => a.accountName.localeCompare(b.accountName));

	const isSomeGroupsSelected =
		selectedGroups.length > 0 && selectedGroups.length < store.groups.length;

	const isAllGroupsSelected =
		selectedGroups.length === store.groups.length &&
		(store.groups.length > 0 || insurance.type === "wc");

	return (
		// Group Dropdown Menu
		<Popover>
			<PopoverTrigger asChild>
				<Button variant={hasGroupError ? "destructive" : "outline"}>
					<UsersIcon />
					{t("admin.insurances.groups.title")}
					{isSomeGroupsSelected && (
						<>
							<Separator orientation="vertical" className="mx-2 h-4" />
							{groupsByAccount.map((account) => {
								return (
									<Badge
										variant="secondary"
										key={account.accountId}
										className="rounded-sm px-1 flex gap-2 font-normal"
									>
										<span className="font-semibold ml-2">
											{account?.accountName}
										</span>
										<span>
											{account.isAll
												? t("admin.insurances.groups.all")
												: account.groups.map((group) => group.name).join(", ")}
										</span>
									</Badge>
								);
							})}
						</>
					)}
					{isAllGroupsSelected && (
						<>
							<Separator orientation="vertical" className="mx-2 h-4" />
							<Badge
								variant="secondary"
								className="rounded-sm px-1 font-normal"
							>
								{t("admin.insurances.groups.all")}
							</Badge>
						</>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent align="start" className="p-0 w-fit">
				<Command className="w-fit">
					<CommandInput
						className="border-none outline-none focus-visible:ring-0"
						placeholder={t("admin.filter.searchPlaceholder")}
					/>
					<CommandList>
						<CommandEmpty>{t("admin.filter.noResults")}</CommandEmpty>
						<CommandGroup>
							{sortedGroups.map((group) => {
								return (
									<CommandItem
										onSelect={() =>
											store.toggleSingleGroupForInsurance(insurance, group.id)
										}
										key={group.id}
									>
										<div
											className={cn(
												"mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-gray-600 dark:border-gray-400",
												selectedGroups.includes(group.id)
													? "bg-gray-900 dark:bg-gray-100 text-primary-foreground"
													: "opacity-50 [&_svg]:invisible",
											)}
										>
											<CheckIcon className="h-4 w-4" />
										</div>
										<div className="justify-between flex flex-grow gap-4">
											<p>{group.name}</p>
											<span className="font-medium">
												{
													store.accounts.find(
														(account) => account.id === group.accountID,
													)?.name
												}
											</span>
										</div>
									</CommandItem>
								);
							})}
						</CommandGroup>
						{selectedGroups.length > 0 && (
							<>
								<CommandSeparator />
								<CommandGroup>
									<CommandItem
										onSelect={() =>
											store.clearGroupsFromInsurance(insurance.id)
										}
										className="justify-center text-center text-poppy dark:text-poppy-60 data-[selected=true]:text-white data-[selected=true]:bg-poppy-60 dark:data-[selected=true]:text-white dark:data-[selected=true]:bg-poppy-60 font-semibold"
									>
										{t("admin.filter.remove")}
									</CommandItem>
								</CommandGroup>
							</>
						)}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
};
